<template>
  <div class="user-list">
    <div v-for="user in users" :key="user.id" class="user-item" :class="{ 'is-active': user.isActive }">
      <div class="user-avatar">
        <img :src="user.avatar || defaultAvatar" :alt="user.name">
        <div class="user-status" :class="{ 'online': user.isOnline }"></div>
      </div>
      <div class="user-info">
        <div class="user-name">{{ user.name }}</div>
        <div class="user-role" v-if="user.role">{{ user.role }}</div>
      </div>
      <div class="user-actions" v-if="showActions">
        <slot name="user-actions" :user="user">
          <!-- 默认操作按钮 -->
          <button 
            v-if="showMuteBtn && user.id !== currentUserId" 
            class="user-action-btn" 
            :class="{ 'muted': user.isMuted }"
            @click="toggleMute(user)"
          >
            <i :class="user.isMuted ? 'fas fa-volume-mute' : 'fas fa-volume-up'"></i>
          </button>
          <button 
            v-if="showReportBtn && user.id !== currentUserId" 
            class="user-action-btn report-btn"
            @click="reportUser(user)"
          >
            <i class="fas fa-flag"></i>
          </button>
        </slot>
      </div>
    </div>
    <div class="empty-message" v-if="users.length === 0">
      {{ emptyMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserList',
  props: {
    users: {
      type: Array,
      default: () => []
    },
    currentUserId: {
      type: [String, Number],
      default: null
    },
    defaultAvatar: {
      type: String,
      default: '/avatar-placeholder.png'
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showMuteBtn: {
      type: Boolean,
      default: true
    },
    showReportBtn: {
      type: Boolean,
      default: true
    },
    emptyMessage: {
      type: String,
      default: '没有用户'
    }
  },
  methods: {
    toggleMute(user) {
      this.$emit('toggle-mute', user);
    },
    reportUser(user) {
      this.$emit('report-user', user);
    }
  }
}
</script>

<style scoped>
.user-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.user-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: var(--card-bg, rgba(255, 255, 255, 0.8));
  border: 1px solid var(--border-color, rgba(203, 213, 225, 0.5));
  transition: background-color 0.3s, box-shadow 0.3s;
}

.user-item.is-active {
  background-color: var(--primary-light, rgba(67, 97, 238, 0.08));
  border-color: var(--primary-color, rgba(67, 97, 238, 0.5));
}

.user-avatar {
  position: relative;
  margin-right: 0.75rem;
}

.user-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--border-color, rgba(203, 213, 225, 0.5));
}

.user-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--status-offline, #9ca3af);
  border: 2px solid var(--card-bg, white);
}

.user-status.online {
  background-color: var(--status-online, #10b981);
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 500;
  color: var(--text-color, #1f2937);
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.875rem;
  color: var(--text-secondary, #64748b);
}

.user-actions {
  display: flex;
  gap: 0.5rem;
}

.user-action-btn {
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;
}

.user-action-btn:hover {
  background-color: var(--btn-hover-bg, rgba(0, 0, 0, 0.05));
  color: var(--text-color, #1f2937);
}

.user-action-btn.muted {
  color: var(--danger-color, #e74c3c);
}

.user-action-btn.report-btn:hover {
  color: var(--warning-color, #f59e0b);
}

.empty-message {
  text-align: center;
  padding: 1rem;
  color: var(--text-secondary, #64748b);
  font-style: italic;
}

/* 暗色主题支持 */
[data-theme="dark"] .user-item {
  background-color: var(--card-bg, rgba(42, 42, 42, 0.8));
  border-color: var(--border-color, rgba(75, 85, 99, 0.5));
}

[data-theme="dark"] .user-name {
  color: var(--text-color, #e5e7eb);
}

[data-theme="dark"] .user-role {
  color: var(--text-secondary, #9ca3af);
}

[data-theme="dark"] .user-avatar img {
  border-color: var(--border-color, rgba(75, 85, 99, 0.5));
}

[data-theme="dark"] .user-status {
  border-color: var(--card-bg, #2a2a2a);
}

[data-theme="dark"] .user-action-btn:hover {
  background-color: var(--btn-hover-bg, rgba(255, 255, 255, 0.05));
  color: var(--text-color, #e5e7eb);
}

[data-theme="dark"] .empty-message {
  color: var(--text-secondary, #9ca3af);
}
</style> 